import {
  checkDevtoolsEnabled,
  checkPrintToConsole,
} from './checkDevtoolsEnabled';
import { safe } from './safe';

type Level = 'debug' | 'info' | 'warn' | 'error';

type Tags = {
  component?: string;
  typeName?: string;
  instanceKey?: string;
  changeId?: number;
  originId?: number;
} & Record<
  string,
  string | string[] | number | number[] | boolean | boolean[] | undefined | null
>;

export type Log = {
  timestamp: number;
  message: string;
  level: Level;
  tags?: Tags;
};

export type Logger = {
  debug: (message: string, tags?: Tags) => void;
  info: (message: string, tags?: Tags) => void;
  warn: (message: string, tags?: Tags) => void;
  error: (message: string, tags?: Tags) => void;
  attachDevtools: (cb: (log: Log) => void) => void;
  withTags: (tags: Tags) => Logger;
};

export const createLogger = ({
  devtoolsEnabled = checkDevtoolsEnabled(),
  printToConsole = checkPrintToConsole(),
  logs = [],
  defaultTags = {},
}: {
  devtoolsEnabled?: boolean;
  printToConsole?: boolean;
  logs?: Log[];
  defaultTags?: Tags;
} = {}): Logger | undefined => {
  if (!devtoolsEnabled && !printToConsole) {
    return undefined;
  }

  let onLog: ((log: Log) => void) | undefined;

  const makeLog = (level: Level) => (message: string, tags?: Tags) => {
    if (!devtoolsEnabled && !printToConsole) {
      return;
    }

    safe(() => {
      const baseMergedTags = {
        ...defaultTags,
        ...tags,
      };

      const mergedTags = baseMergedTags;

      const now = Date.now();

      if (devtoolsEnabled) {
        const log = {
          timestamp: now,
          message,
          level,
          tags: mergedTags,
        };

        logs.push(log);
        onLog?.(log);
      }

      if (printToConsole) {
        const { component, ...restTags } = mergedTags;

        const baseString = `${now} [${component || 'unknown'}] ${message}`;

        console[level](
          `${baseString} ${
            mergedTags?.instanceKey ? `| ${mergedTags.instanceKey}` : ''
          } | ${Object.entries(restTags)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ')}`
        );
      }
    });
  };

  return {
    debug: makeLog('debug'),
    info: makeLog('info'),
    warn: makeLog('warn'),
    error: makeLog('error'),
    attachDevtools: (cb: (log: Log) => void) => {
      onLog = cb;
    },
    withTags: (tags: Tags) => {
      const logger = createLogger({
        devtoolsEnabled,
        printToConsole,
        logs,
        defaultTags: { ...defaultTags, ...tags },
      }) as Logger;

      if (onLog) {
        logger.attachDevtools(onLog);
      }

      return logger;
    },
  };
};
